import { bestAverage, mean } from '@/ride/metrics';
import type { FtpProposal, Lap, RideSample } from '@/ride/model';
import { isValidFtp } from '@/store/settingsModel';
import type { Workout } from './model';
import { FTP20_LABEL, type RunnerView } from './runner';

/** The 20-min block must have been ridden (almost) completely to count. */
export const FTP20_MIN_BLOCK_SEC = 19 * 60;

/**
 * FTP from a completed FTP test (SPEC §7.5). The result replaces the rider's FTP automatically, so only a
 * test that was actually finished counts:
 * - ramp test: ridden to exhaustion (runner ended on the cadence criterion) → FTP = 0.75 × best 1-min power,
 * - 20-min test: the 20-min block was ridden for ≥ 19 min → FTP = 0.95 × average power of the block.
 * Returns null for an aborted/incomplete test or an implausible result.
 */
export function computeFtpProposal(
  workout: Workout,
  samples: RideSample[],
  laps: Lap[],
  doneReason: RunnerView['doneReason'],
): FtpProposal | null {
  const power = samples.map((s) => s.power);
  let p: FtpProposal | null = null;
  if (workout.special === 'ramp-test') {
    if (doneReason !== 'ramp-cadence') return null;
    const best1 = bestAverage(power, 60);
    if (best1 > 0) p = { kind: 'ramp-test', basisWatts: best1, proposedFtp: Math.round(0.75 * best1) };
  } else if (workout.special === 'ftp20') {
    const lap = laps.find((l) => l.label === FTP20_LABEL);
    if (!lap || lap.endEl - lap.startEl < FTP20_MIN_BLOCK_SEC) return null;
    const avg = mean(samples.filter((s) => s.t >= lap.startT && s.t <= lap.endT).map((s) => s.power));
    if (avg > 0) p = { kind: 'ftp20', basisWatts: avg, proposedFtp: Math.round(0.95 * avg) };
  }
  return p && isValidFtp(p.proposedFtp) ? p : null;
}
